import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Event, EventStatus, AppState, User, ThemeType, ConnectionStatus, ChatMessage } from '../types';
import { format } from 'date-fns';
import { settingsAPI } from '../services/api';

interface AppStore extends AppState {
  // Events
  events: Event[];
  filteredEvents: Event[];
  selectedDate: Date;
  currentEvent: Event | null;
  
  // UI State
  isLoading: boolean;
  isOffline: boolean;
  isEventModalOpen: boolean;
  isChatOpen: boolean;
  connectionStatus: ConnectionStatus;
  
  // User & Settings
  user: User | null;
  theme: ThemeType;
  reminders: Set<string>;
  settings: Record<string, any>;
  
  // Chat
  chatMessages: ChatMessage[];
  isActyTyping: boolean;
  
  // Actions
  setEvents: (events: Event[]) => void;
  addEvent: (event: Event) => void;
  updateEvent: (eventId: string, updates: Partial<Event>) => void;
  removeEvent: (eventId: string) => void;
  setSelectedDate: (date: Date) => void;
  filterEventsByDate: (date: Date) => void;
  setCurrentEvent: (event: Event | null) => void;
  
  // UI Actions
  setLoading: (loading: boolean) => void;
  setOffline: (offline: boolean) => void;
  setEventModalOpen: (open: boolean) => void;
  setChatOpen: (open: boolean) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  
  // User Actions
  setUser: (user: User | null) => void;
  setTheme: (theme: ThemeType) => void;
  
  // Settings Actions
  setSettings: (settings: Record<string, any>) => void;
  loadPublicSettings: () => Promise<void>;
  
  // Reminders
  toggleReminder: (eventId: string) => void;
  isReminderSet: (eventId: string) => boolean;
  
  // Chat Actions
  addChatMessage: (message: ChatMessage) => void;
  clearChat: () => void;
  setActyTyping: (typing: boolean) => void;
  
  // Utility Actions
  getEventsByStatus: (status: EventStatus) => Event[];
  getUpcomingEvents: () => Event[];
  getActiveEvents: () => Event[];
  isEventActive: (event: Event) => boolean;
  getEventProgress: (event: Event) => number;
  reset: () => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial State
      events: [],
      filteredEvents: [],
      selectedDate: new Date(),
      currentEvent: null,
      isLoading: false,
      isOffline: false,
      isEventModalOpen: false,
      isChatOpen: false,
      connectionStatus: 'online',
      user: null,
      theme: ThemeType.MANGA,
      reminders: new Set<string>(),
      settings: {},
      chatMessages: [
        {
          id: '1',
          content: '¡Hola! Soy Acty 🎭, tu asistente virtual. ¿En qué puedo ayudarte con los eventos de hoy?',
          sender: 'acty',
          timestamp: new Date(),
        }
      ],
      isActyTyping: false,

      // Event Actions
      setEvents: (events) => {
        set({ events });
        get().filterEventsByDate(get().selectedDate);
      },

      addEvent: (event) => {
        const events = [...get().events, event];
        set({ events });
        get().filterEventsByDate(get().selectedDate);
      },

      updateEvent: (eventId, updates) => {
        const events = get().events.map(event =>
          event.id === eventId ? { ...event, ...updates } : event
        );
        set({ events });
        get().filterEventsByDate(get().selectedDate);
        
        // Update current event if it's the one being updated
        const currentEvent = get().currentEvent;
        if (currentEvent && currentEvent.id === eventId) {
          set({ currentEvent: { ...currentEvent, ...updates } });
        }
      },

      removeEvent: (eventId) => {
        const events = get().events.filter(event => event.id !== eventId);
        set({ events });
        get().filterEventsByDate(get().selectedDate);
        
        // Clear current event if it's the one being removed
        const currentEvent = get().currentEvent;
        if (currentEvent && currentEvent.id === eventId) {
          set({ currentEvent: null, isEventModalOpen: false });
        }
      },

      setSelectedDate: (date) => {
        set({ selectedDate: date });
        get().filterEventsByDate(date);
      },

      filterEventsByDate: (date) => {
        const events = get().events;
        const dateStr = format(date, 'yyyy-MM-dd');
        const filteredEvents = events.filter(event => {
          const eventDateStr = format(new Date(event.startTime), 'yyyy-MM-dd');
          return eventDateStr === dateStr;
        }).sort((a, b) => {
          // Sort by start time
          return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        });
        set({ filteredEvents });
      },

      setCurrentEvent: (event) => {
        set({ 
          currentEvent: event,
          isEventModalOpen: !!event 
        });
      },

      // UI Actions
      setLoading: (loading) => set({ isLoading: loading }),
      
      setOffline: (offline) => set({ 
        isOffline: offline,
        connectionStatus: offline ? 'offline' : 'online'
      }),
      
      setEventModalOpen: (open) => {
        set({ isEventModalOpen: open });
        if (!open) {
          set({ currentEvent: null });
        }
      },
      
      setChatOpen: (open) => set({ isChatOpen: open }),
      
      setConnectionStatus: (status) => set({ 
        connectionStatus: status,
        isOffline: status === 'offline'
      }),

      // User Actions
      setUser: (user) => set({ user }),
      
      setTheme: (theme) => set({ theme }),

      // Settings Actions
      setSettings: (settings) => set({ settings }),

      loadPublicSettings: async () => {
        try {
          const publicSettings = await settingsAPI.getPublicSettings();
          set({ settings: publicSettings });
        } catch (error) {
          console.warn('Failed to load public settings:', error);
        }
      },

      // Reminder Actions
      toggleReminder: (eventId) => {
        const reminders = new Set(get().reminders);
        if (reminders.has(eventId)) {
          reminders.delete(eventId);
        } else {
          reminders.add(eventId);
        }
        set({ reminders });
      },

      isReminderSet: (eventId) => {
        return get().reminders.has(eventId);
      },

      // Chat Actions
      addChatMessage: (message) => {
        const chatMessages = [...get().chatMessages, message];
        set({ chatMessages });
      },

      clearChat: () => {
        set({ 
          chatMessages: [
            {
              id: 'welcome',
              content: '¡Hola! Soy Acty 🎭, tu asistente virtual. ¿En qué puedo ayudarte con los eventos de hoy?',
              sender: 'acty',
              timestamp: new Date(),
            }
          ] 
        });
      },

      setActyTyping: (typing) => set({ isActyTyping: typing }),

      // Utility Functions
      getEventsByStatus: (status) => {
        return get().events.filter(event => event.status === status);
      },

      getUpcomingEvents: () => {
        const now = new Date();
        return get().events.filter(event => {
          const startTime = new Date(event.startTime);
          return startTime > now;
        }).sort((a, b) => {
          return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        });
      },

      getActiveEvents: () => {
        const now = new Date();
        return get().events.filter(event => {
          const startTime = new Date(event.startTime);
          const endTime = new Date(event.endTime);
          return startTime <= now && endTime >= now;
        });
      },

      isEventActive: (event) => {
        const now = new Date();
        const startTime = new Date(event.startTime);
        const endTime = new Date(event.endTime);
        return startTime <= now && endTime >= now;
      },

      getEventProgress: (event) => {
        const now = new Date();
        const startTime = new Date(event.startTime);
        const endTime = new Date(event.endTime);
        
        if (now < startTime) return 0;
        if (now > endTime) return 100;
        
        const totalDuration = endTime.getTime() - startTime.getTime();
        const elapsed = now.getTime() - startTime.getTime();
        return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
      },

      reset: () => {
        set({
          events: [],
          filteredEvents: [],
          selectedDate: new Date(),
          currentEvent: null,
          isLoading: false,
          isOffline: false,
          isEventModalOpen: false,
          isChatOpen: false,
          connectionStatus: 'online',
          reminders: new Set<string>(),
          chatMessages: [
            {
              id: 'welcome',
              content: '¡Hola! Soy Acty 🎭, tu asistente virtual. ¿En qué puedo ayudarte con los eventos de hoy?',
              sender: 'acty',
              timestamp: new Date(),
            }
          ],
          isActyTyping: false,
        });
      },
    }),
    {
      name: 'pro-act-app-store',
      partialize: (state) => ({
        user: state.user,
        theme: state.theme,
        reminders: Array.from(state.reminders), // Convert Set to Array for serialization
        // Don't persist selectedDate - always use current date
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Convert Array back to Set
          state.reminders = new Set(state.reminders as unknown as string[]);
          // Always use today's date instead of persisted date
          state.selectedDate = new Date();
        }
      },
    }
  )
);